/**
 * Jul-2026 (User-Wunsch) — KI-Anreicherung prüfen (/dealer/wms/anreicherung).
 * Vorher/Nachher-Ansicht der automatisch angereicherten Artikel:
 * Web-Fotos + Namensänderungen freigeben oder verwerfen. Zusätzlich:
 * Preisetiketten mit Barcode (PDF) je Standort drucken.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Loader2, Check, X, ExternalLink, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrichmentReviewPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [reviewLoc, setReviewLoc] = useState(null);
    const photoUrl = (p) => `${API}/wms/photos/${p}?auth=${sessionStorage.getItem("tw_token") || ""}`;

    const load = () => api.get("/articles/ai-enrich/review")
        .then((r) => {
            setData(r.data);
            setReviewLoc((prev) => {
                if (prev !== null) return prev;
                const mine = (r.data.locations || []).find((l) => l.dealer_id === r.data.current_dealer_id);
                return mine ? mine.dealer_id : "";
            });
        })
        .catch(() => toast.error("Review-Liste konnte nicht geladen werden"))
        .finally(() => setLoading(false));
    useEffect(() => { load(); }, []);

    const act = async (id, action) => {
        setBusyId(id);
        try {
            const r = await api.post(`/articles/ai-enrich/review/${id}`, { action });
            toast.success(action === "approve" ? "Freigegeben" : `Verworfen — ${r.data.photos_removed || 0} Web-Foto(s) entfernt${r.data.name_restored ? ", Name zurückgesetzt" : ""}`);
            setData((d) => ({ ...d, items: d.items.filter((i) => i.article_id !== id) }));
        } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
        finally { setBusyId(null); }
    };

    const approveAll = async () => {
        if (!window.confirm(`Alle ${items.length} Artikel freigeben?`)) return;
        setBulkBusy(true);
        try {
            for (const it of items) await api.post(`/articles/ai-enrich/review/${it.article_id}`, { action: "approve" });
            toast.success("Alle freigegeben");
            load();
        } catch { toast.error("Teilweise fehlgeschlagen"); load(); }
        finally { setBulkBusy(false); }
    };

    const allItems = data?.items || [];
    const items = reviewLoc ? allItems.filter((i) => i.dealer_id === reviewLoc) : allItems;
    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-5xl mx-auto px-4 py-8" data-testid="enrich-review-page">
                <Link to="/dealer/wms" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#111827] mb-4"><ArrowLeft className="w-4 h-4" /> Warenwirtschaft</Link>
                <h1 className="font-display font-black text-3xl text-[#111827] mb-1 flex items-center gap-2">
                    <Sparkles className="w-7 h-7 text-[#FFCC00]" /> KI-Anreicherung prüfen
                </h1>
                <p className="text-sm text-gray-500 mb-4">Vorher/Nachher der automatisch angereicherten Artikel — Web-Fotos & Namensvorschläge freigeben oder verwerfen.</p>

                {/* Nov-2026 (User-Wunsch): Preisetiketten sind in die Warenwirtschaft
                    umgezogen — eigener Tab „Preisetiketten" unter /dealer/wms. */}

                <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500" data-testid="enrich-review-count">{items.length} Artikel zu prüfen</span>
                        {reviewLoc !== null && (
                            <select value={reviewLoc} onChange={(e) => setReviewLoc(e.target.value)}
                                className="h-8 text-xs border-2 border-gray-200 rounded-sm px-2" data-testid="review-location-select">
                                <option value="">Alle Standorte ({allItems.length})</option>
                                {(data?.locations || []).map((l) => <option key={l.dealer_id} value={l.dealer_id}>{l.label}</option>)}
                            </select>
                        )}
                    </div>
                    {items.length > 1 && (
                        <Button type="button" size="sm" variant="outline" onClick={approveAll} disabled={bulkBusy} className="h-8 text-xs border-2" data-testid="enrich-approve-all">
                            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCheck className="w-3.5 h-3.5 mr-1" /> Alle freigeben</>}
                        </Button>
                    )}
                </div>

                {loading ? (
                    <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                ) : items.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-sm text-gray-400" data-testid="enrich-review-empty">
                        Alles geprüft — keine offenen Anreicherungen. Neue Läufe erscheinen hier automatisch.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {items.map((it) => (
                            <div key={it.article_id} className="bg-white border border-gray-200 rounded-sm p-4" data-testid={`enrich-item-${it.article_id}`}>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <div className="flex gap-1.5 shrink-0">
                                        {(it.photos || []).slice(0, 3).map((p) => (
                                            <a key={p.photo_id} href={photoUrl(p.path)} target="_blank" rel="noopener noreferrer" title={`Quelle: ${p.source_domain || "Web"}`}>
                                                <img src={photoUrl(p.path)} alt="" loading="lazy" decoding="async" className="w-16 h-16 object-cover border border-gray-200 rounded-sm" />
                                            </a>
                                        ))}
                                        {!(it.photos || []).length && <div className="w-16 h-16 border border-dashed border-gray-200 rounded-sm flex items-center justify-center text-[9px] text-gray-300">kein Foto</div>}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[10px] font-bold text-gray-400">{it.sku} · {it.location_label || "—"}{it.location ? ` · Lagerort ${it.location}` : ""} · Bestand {it.stock_qty ?? 0}{it.ownership === "consignment" ? " · Kommission" : ""}</div>
                                        {it.name_changed && it.before_name ? (
                                            <div className="text-sm mt-0.5">
                                                <span className="text-gray-400 line-through">{it.before_name}</span>
                                                <span className="mx-1.5 text-gray-300">→</span>
                                                <span className="font-bold text-emerald-700">{it.name}</span>
                                            </div>
                                        ) : (
                                            <div className="text-sm font-bold mt-0.5">{it.name}</div>
                                        )}
                                        <div className="text-[10px] text-gray-400 mt-0.5">
                                            {(it.photos || []).length} Web-Foto(s){it.photos?.[0]?.source_domain ? ` von ${it.photos[0].source_domain}` : ""}
                                            {it.name_source_url && <a href={it.name_source_url} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline ml-1.5 inline-flex items-center gap-0.5">Namensquelle <ExternalLink className="w-2.5 h-2.5" /></a>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <Button type="button" size="sm" onClick={() => act(it.article_id, "approve")} disabled={busyId === it.article_id}
                                            className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid={`enrich-approve-${it.article_id}`}>
                                            {busyId === it.article_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Freigeben</>}
                                        </Button>
                                        <Button type="button" size="sm" variant="outline" onClick={() => act(it.article_id, "reject")} disabled={busyId === it.article_id}
                                            className="h-9 border-2 text-red-600 hover:bg-red-50 font-bold" data-testid={`enrich-reject-${it.article_id}`}>
                                            <X className="w-4 h-4 mr-1" /> Verwerfen
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
